import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  formatCurrency,
  formatPercent,
  formatDateRange,
} from "@/utils/formatters";
import { formatDate } from "@/utils/timezone";
import ColoredPercent from "../ColoredPercent";
import ColoredCurrency from "../ColoredCurrency";
import TradeHistory from "./TradeHistory";
import TradeBreakdown from "./TradeBreakdown";
import PipeSeparated from "../PipeSeparated";
import api from "@/api";

const PerformanceAnalytics = ({
  accountId,
  account,
  holdings,
  initialPeriod = "weekly",
  autoSelectToday = false,
  initialMode = "periods",
  chartMetric = "account_balance",
}) => {
  const [viewMode, setViewMode] = useState(initialMode); // 'periods' or 'chart'
  const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod);
  const [periodSummaries, setPeriodSummaries] = useState([]);
  const [selectedPeriodDetail, setSelectedPeriodDetail] = useState(null);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [currentPerformance, setCurrentPerformance] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState("breakdown");
  const [isFading, setIsFading] = useState(false);

  const fetchPeriodSummaries = async () => {
    if (!accountId) return;

    try {
      setLoading(true);
      // Try new schema first (accounts), fall back to old schema (accounts)
      let response;
      try {
        response = await api.get(
          `/accounts/${accountId}/performance-summaries/${selectedPeriod}`
        );
      } catch (error) {
        if (error.response?.status === 404) {
          response = await api.get(
            `/accounts/${accountId}/performance-summaries/${selectedPeriod}`
          );
        } else {
          throw error;
        }
      }
      setPeriodSummaries(response.data);

      // Auto-select first period when user clicks Daily/Weekly/Monthly
      const firstPeriod = response.data[0];
      fetchPeriodDetail(firstPeriod.start_date, firstPeriod.end_date, 0);
    } catch (error) {
      console.error("Error fetching period summaries:", error);
      setPeriodSummaries([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPerformance = async () => {
    if (!accountId) return;

    try {
      const response = await api.get(
        `/accounts/${accountId}/current-performance`
      );
      setCurrentPerformance(response.data);
    } catch (error) {
      console.error("Error fetching current performance:", error);
    }
  };

  const fetchPeriodDetail = async (startDate, endDate, index, periodLabel) => {
    try {
      // If there's already detail data, fade it out first
      if (selectedPeriodDetail) {
        setIsFading(true);
        await new Promise((resolve) => setTimeout(resolve, 200)); // Wait for fade out
      }

      setLoadingDetail(true);
      setSelectedPeriodIndex(index);
      // Try new schema first (accounts), fall back to old schema (accounts)
      let response;
      try {
        response = await api.get(`/accounts/${accountId}/performance-detail`, {
          params: { start_date: startDate, end_date: endDate },
        });
      } catch (error) {
        if (error.response?.status === 404) {
          response = await api.get(
            `/accounts/${accountId}/performance-detail`,
            {
              params: { start_date: startDate, end_date: endDate },
            }
          );
        } else {
          throw error;
        }
      }
      // Attach the period label to the response data
      setSelectedPeriodDetail({ ...response.data, period_label: periodLabel });

      // Fade back in with new content
      setTimeout(() => {
        setIsFading(false);
      }, 50); // Small delay to ensure DOM update before fade in
    } catch (error) {
      console.error("Error fetching period detail:", error);
      setSelectedPeriodDetail(null);
      setIsFading(false);
    } finally {
      setLoadingDetail(false);

      // Smooth scroll down to bottom of page so details are visible
      // setTimeout(() => {
      //   window.scrollTo({
      //     top: document.documentElement.scrollHeight,
      //     behavior: "smooth",
      //   });
      // }, 100); // Small delay to ensure DOM is updated
    }
  };

  useEffect(() => {
    fetchPeriodSummaries();
    fetchCurrentPerformance();
  }, [accountId, selectedPeriod]);

  // Removed auto-selection useEffect - now handled in fetchPeriodSummaries

  const periodLabels = {
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
  };

  return (
    <div style={{ padding: "1.5rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
        }}
      >
        <div>
          <h3 className="section-title">Performance Analytics</h3>
          <p
            className="text-muted"
            style={{
              fontSize: "0.875rem",
              marginTop: "0.5rem",
            }}
          >
            Detailed P&L breakdown showing what drives your returns
          </p>
        </div>

        {/* Period Selection */}
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {["daily", "weekly", "monthly"].map((period) => (
            <button
              key={period}
              className={`btn ${
                selectedPeriod === period ? "btn-primary" : "btn-secondary"
              }`}
              onClick={() => {
                setSelectedPeriod(period);
              }}
              data-testid={`period-${period}`}
              style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
            >
              {periodLabels[period]}
            </button>
          ))}
        </div>
      </div>

      {loading && periodSummaries.length === 0 && (
        <div className="loading">
          <div className="spinner"></div>
          Loading {periodLabels[selectedPeriod].toLowerCase()} performance...
        </div>
      )}

      {periodSummaries.length > 0 && (
        <div>
          {/* Period Summary Widgets */}
          <div style={{ marginBottom: "2rem" }}>
            <h4 style={{ marginBottom: "1rem" }}>
              Last 5 {periodLabels[selectedPeriod]} Periods
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "1rem",
              }}
            >
              {periodSummaries.map((summary, index) => (
                <div
                  key={index}
                  className="account-card"
                  onClick={() =>
                    fetchPeriodDetail(
                      summary.start_date,
                      summary.end_date,
                      index,
                      summary.period_label
                    )
                  }
                  style={{
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    border:
                      selectedPeriodIndex === index
                        ? "3px solid #4299e1"
                        : summary.period_pnl >= 0
                        ? "2px solid #d1fae5"
                        : "2px solid #fee2e2",
                    boxShadow:
                      selectedPeriodIndex === index
                        ? "0 8px 25px rgba(0,0,0,0.15)"
                        : "0 4px 6px rgba(0,0,0,0.05)",
                  }}
                  data-testid={`period-summary-${index}`}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "0.75rem",
                    }}
                  >
                    <div>
                      <div className="stat-label">{summary.period_label}</div>
                      {selectedPeriod !== "daily" &&
                        formatDateRange(
                          summary.start_date,
                          summary.end_date
                        ) && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.75rem" }}
                          >
                            {formatDateRange(
                              summary.start_date,
                              summary.end_date
                            )}
                          </div>
                        )}
                    </div>
                    <TradesWidget
                      totalTrades={summary.trades_count}
                      winsCount={summary.wins_count}
                      lossesCount={summary.losses_count}
                    />
                  </div>

                  {/* Total P&L */}
                  <div
                    style={{
                      fontSize: "1.25rem",
                      marginBottom: "0.5rem",
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <ColoredCurrency value={summary.period_pnl} />
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      <ColoredPercent value={summary.period_pnl_percent} />
                    </span>
                  </div>

                  {/* P&L Breakdown */}
                  {(summary.realized_pnl !== undefined ||
                    summary.unrealized_pnl !== undefined) && (
                    <div
                      style={{
                        fontSize: "0.75rem",
                        lineHeight: "1.4",
                      }}
                    >
                      <div style={{ display: "flex" }}>
                        <span
                          style={{
                            width: "50%",
                            textAlign: "right",
                            paddingRight: "0.25rem",
                          }}
                        >
                          Realized:
                        </span>
                        <span style={{ width: "50%", textAlign: "left" }}>
                          <ColoredCurrency value={summary.realized_pnl || 0} />
                        </span>
                      </div>
                      <div style={{ display: "flex" }}>
                        <span
                          style={{
                            width: "50%",
                            textAlign: "right",
                            paddingRight: "0.25rem",
                          }}
                        >
                          Unrealized:
                        </span>
                        <span style={{ width: "50%", textAlign: "left" }}>
                          <ColoredCurrency
                            value={summary.unrealized_pnl || 0}
                          />
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Top contributor - hidden for now */}
                  {false && summary.top_contributor && (
                    <div
                      className="text-muted"
                      style={{
                        fontSize: "0.75rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      Top:{" "}
                      <Link
                        to={`/ticker/${summary.top_contributor.ticker}?accountId=${accountId}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <strong style={{ cursor: "pointer" }}>
                          {summary.top_contributor.ticker}
                        </strong>
                      </Link>{" "}
                      <ColoredCurrency value={summary.top_contributor.pnl} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* P&L Details Section */}
          {selectedPeriodDetail ? (
            <div
              id="performance-details-panel"
              className="account-card"
              style={{
                marginTop: "2rem",
                opacity: isFading ? 0 : 1,
                transition: "opacity 200ms ease-in-out",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <div>
                  <h4 style={{ marginBottom: "0.5rem" }}>
                    Period Performance Details
                  </h4>
                  <div
                    className="stat-label"
                    style={{ marginBottom: "0.25rem" }}
                  >
                    {selectedPeriodDetail.period_label}
                  </div>
                  {selectedPeriod !== "daily" &&
                    formatDateRange(
                      selectedPeriodDetail.period_start_date,
                      selectedPeriodDetail.period_end_date
                    ) && (
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {formatDateRange(
                          selectedPeriodDetail.period_start_date,
                          selectedPeriodDetail.period_end_date
                        )}
                      </div>
                    )}
                </div>
                <button
                  onClick={() => {
                    setSelectedPeriodDetail(null);
                    setSelectedPeriodIndex(null);
                  }}
                  className="text-muted"
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                    padding: "0.5rem",
                  }}
                  data-testid="close-details-button"
                >
                  ×
                </button>
              </div>

              {/* Period Summary Stats - HIDDEN */}
              <div
                style={{
                  display: "none",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "1rem",
                  marginBottom: "2rem",
                }}
              >
                <div
                  className="stat-card-subtle"
                  style={{
                    textAlign: "center",
                  }}
                >
                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.875rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Total P&L
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                    }}
                  >
                    <ColoredCurrency value={selectedPeriodDetail.period_pnl} />
                  </div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    <ColoredPercent
                      value={selectedPeriodDetail.period_pnl_percent}
                    />
                  </div>
                </div>

                {/* Trade P&L Widget - sum of all trade contributions */}
                <div
                  className="stat-card-subtle"
                  style={{
                    textAlign: "center",
                  }}
                >
                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.875rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Trade P&L
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                    }}
                  >
                    <ColoredCurrency
                      value={
                        selectedPeriodDetail.contributors &&
                        selectedPeriodDetail.contributors.length > 0
                          ? selectedPeriodDetail.contributors.reduce(
                              (sum, c) => sum + (c.period_pnl || 0),
                              0
                            )
                          : 0
                      }
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    <ColoredPercent
                      value={
                        selectedPeriodDetail.contributors &&
                        selectedPeriodDetail.contributors.length > 0 &&
                        selectedPeriodDetail.period_start_value > 0
                          ? (selectedPeriodDetail.contributors.reduce(
                              (sum, c) => sum + (c.period_pnl || 0),
                              0
                            ) /
                              selectedPeriodDetail.period_start_value) *
                            100
                          : 0
                      }
                    />
                  </div>
                </div>

                {/* Trades Widget */}
                <div
                  className="stat-card-subtle"
                  style={{
                    textAlign: "center",
                  }}
                >
                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.875rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Trades
                  </div>
                  <div style={{ fontWeight: "600" }}>
                    {selectedPeriodDetail.contributors &&
                    selectedPeriodDetail.contributors.length > 0 ? (
                      <PipeSeparated
                        left={
                          <span className="positive">
                            {
                              selectedPeriodDetail.contributors.filter(
                                (c) => c.period_pnl > 0
                              ).length
                            }
                          </span>
                        }
                        right={
                          <span className="negative">
                            {
                              selectedPeriodDetail.contributors.filter(
                                (c) => c.period_pnl < 0
                              ).length
                            }
                          </span>
                        }
                        fontSize="1.5rem"
                        pipeFontSize="1.25rem"
                      />
                    ) : (
                      <span style={{ color: "var(--stat-label-color)" }}>
                        0
                      </span>
                    )}
                  </div>
                  {selectedPeriodDetail.contributors &&
                  selectedPeriodDetail.contributors.length > 0 ? (
                    <PipeSeparated
                      left="wins"
                      right="losses"
                      fontSize="0.75rem"
                      className="text-muted"
                      style={{ marginTop: "0.25rem" }}
                    />
                  ) : (
                    <div
                      className="text-muted"
                      style={{
                        fontSize: "0.75rem",
                        marginTop: "0.25rem",
                      }}
                    >
                      No trades
                    </div>
                  )}
                </div>
              </div>

              {/* Trade Details Tabs */}
              {selectedPeriodDetail.contributors &&
                selectedPeriodDetail.contributors.length > 0 && (
                  <div>
                    {/* Tab Navigation */}
                    <div
                      className="border-subtle"
                      style={{
                        display: "flex",
                        borderBottom: "2px solid",
                        marginBottom: "1.5rem",
                      }}
                    >
                      <button
                        onClick={() => setActiveDetailTab("breakdown")}
                        className={`tab-button ${
                          activeDetailTab === "breakdown" ? "active" : ""
                        }`}
                        style={{
                          padding: "0.75rem 1.5rem",
                          fontWeight:
                            activeDetailTab === "breakdown" ? "600" : "500",
                          fontSize: "0.875rem",
                        }}
                      >
                        Trade Breakdown
                      </button>
                      <button
                        onClick={() => setActiveDetailTab("history")}
                        className={`tab-button ${
                          activeDetailTab === "history" ? "active" : ""
                        }`}
                        style={{
                          padding: "0.75rem 1.5rem",
                          fontWeight:
                            activeDetailTab === "history" ? "600" : "500",
                          fontSize: "0.875rem",
                        }}
                      >
                        Trade History
                      </button>
                    </div>

                    {/* Tab Content */}
                    {activeDetailTab === "breakdown" && (
                      <TradeBreakdown
                        contributors={selectedPeriodDetail.contributors}
                        accountId={accountId}
                      />
                    )}

                    {activeDetailTab === "history" && (
                      /* Trade History Tab */
                      <TradeHistory
                        accountId={accountId}
                        initialStartDate={
                          selectedPeriodDetail.period_start_date?.split("T")[0]
                        }
                        initialEndDate={
                          selectedPeriodDetail.period_end_date?.split("T")[0]
                        }
                        showFilters={true}
                        showPagination={false}
                        title=""
                        emptyMessage="No trades found for this period"
                      />
                    )}
                  </div>
                )}
            </div>
          ) : (
            <div
              className="stat-card-subtle text-muted"
              style={{
                textAlign: "center",
                padding: "2rem",
                marginTop: "2rem",
              }}
            >
              <h4 className="text-primary" style={{ marginBottom: "0.5rem" }}>
                Click any period above to see detailed P&L breakdown
              </h4>
              <p style={{ fontSize: "0.875rem" }}>
                View exactly which trades contributed to gains and losses during
                each {selectedPeriod.slice(0, -2)} period
              </p>
            </div>
          )}

          {/* Loading state for details */}
          {loadingDetail && (
            <div className="loading" style={{ marginTop: "2rem" }}>
              <div className="spinner"></div>
              Loading period details...
            </div>
          )}
        </div>
      )}

      {!loading && periodSummaries.length === 0 && (
        <div
          className="text-muted"
          style={{ textAlign: "center", padding: "3rem" }}
        >
          <h3>No Performance Data</h3>
          <p>
            Performance analytics will be available once you have trading
            history
          </p>
        </div>
      )}
    </div>
  );
};

// Modal component removed - details now show inline

// Enhanced Trades Widget Component
const TradesWidget = ({ totalTrades, winsCount, lossesCount }) => {
  // Always prioritize the stable summary data (winsCount, lossesCount)
  // This prevents flickering when widgets are selected/deselected
  // Show wins|losses format even when both are 0 for consistency
  if (winsCount !== undefined && lossesCount !== undefined) {
    return (
      <div
        className="bg-card-subtle"
        style={{
          padding: "0.25rem 0.5rem",
          borderRadius: "6px",
          fontWeight: "600",
        }}
      >
        <PipeSeparated
          left={<span className="positive">{winsCount}</span>}
          right={<span className="negative">{lossesCount}</span>}
          fontSize="0.75rem"
          gap="0.25rem"
          minWidth="1.5rem"
        />
      </div>
    );
  }

  // Fallback to simple trades count when no wins/losses data available
  return (
    <div
      style={{
        background: "#f3f4f6",
        color: "#374151",
        padding: "0.25rem 0.5rem",
        borderRadius: "6px",
        fontSize: "0.75rem",
        fontWeight: "600",
      }}
    >
      {totalTrades} trades
    </div>
  );
};

export default PerformanceAnalytics;
